import React, { useState, useEffect, useCallback } from 'react';
import { useUser } from '../context/UserContext';
import DishForm from '../components/AddDishForm';
import { PlusCircleIcon, TrashIcon, PencilSquareIcon, ArrowPathIcon } from '@heroicons/react/24/outline';
import { format } from 'date-fns';

const DishList = () => {
    const { user, authFetch } = useUser();
    const [dishes, setDishes] = useState([]);
    const [loadingDishes, setLoadingDishes] = useState(true);
    const [errorDishes, setErrorDishes] = useState(null);
    const [deletingDishId, setDeletingDishId] = useState(null);
    const [showForm, setShowForm] = useState(false);
    const [editingDish, setEditingDish] = useState(null);
    const [isRepeatingMenu, setIsRepeatingMenu] = useState(false);
    const [repeatMenuStatus, setRepeatMenuStatus] = useState({ message: null, type: null });

    const fetchDishes = useCallback(async () => {
        setLoadingDishes(true);
        setErrorDishes(null);
        try {
            const data = await authFetch('/dishes');
            const today = format(new Date(), 'dd.MM.yyyy');

            const filtered = user?.role === 'user'
                ? data.filter(d => d.days?.includes(today))
                : data;

            setDishes(filtered || []);
        } catch (error) {
            console.error("Failed to fetch dishes:", error);
            setErrorDishes('Не удалось загрузить список блюд.');
            setDishes([]);
        } finally {
            setLoadingDishes(false);
        }
    }, [authFetch, user]);

    useEffect(() => {
        fetchDishes();
    }, [fetchDishes]);

    const isAdmin = user?.role === 'admin';

    const handleAddClick = () => {
        setEditingDish(null);
        setShowForm(true);
    };

    const handleEditClick = (dish) => {
        setEditingDish(dish);
        setShowForm(true);
    };

    const handleCloseForm = () => {
        setShowForm(false);
        setEditingDish(null);
    };

    const handleFormSubmit = (updatedOrNewDish) => {
        handleCloseForm();
        fetchDishes();
    };

    const handleDeleteDish = async (dishId) => {
        if (!dishId) return;

        if (!window.confirm('Вы уверены, что хотите удалить это блюдо? Отменить действие будет невозможно.')) {
            return;
        }

        setDeletingDishId(dishId);
        setErrorDishes(null);

        try {
            await authFetch(`/dishes/${dishId}`, {
                method: 'DELETE',
            });
            setDishes(currentDishes => currentDishes.filter(d => d.id !== dishId));
        } catch (error) {
            console.error("Failed to delete dish:", error);
            setErrorDishes(`Не удалось удалить блюдо: ${error.message || 'ошибка сервера'}`);
        } finally {
            setDeletingDishId(null);
        }
    };

    const handleRepeatAllMenu = async () => {
        if (!window.confirm('Повторить меню для всех блюд? Будут добавлены новые даты (+14 дней), а прошедшие — удалены для всех блюд сразу.')) {
            return;
        }

        setIsRepeatingMenu(true);
        setRepeatMenuStatus({ message: null, type: null });
        setErrorDishes(null);

        try {
            const responseData = await authFetch('/dishes/repeat-all', {
                method: 'POST',
            });
            
            setRepeatMenuStatus({ 
                message: responseData.message || `Успешно обновлено ${responseData.updatedCount} блюд!`, 
                type: 'success' 
            });
            
            // Обновляем список блюд после успешного повторения
            fetchDishes();

        } catch (err) {
            console.error('Failed to repeat all dishes:', err);
            setRepeatMenuStatus({ 
                message: err.message || 'Не удалось повторить меню для всех блюд.', 
                type: 'error' 
            });
        } finally {
            setIsRepeatingMenu(false);
            setTimeout(() => setRepeatMenuStatus({ message: null, type: null }), 5000);
        }
    };

    return (
        <div className="text-gray-200 p-4 md:p-8">
            <div className="w-full max-w-6xl mx-auto space-y-8">
                <div className="flex justify-between items-center mb-6 pb-4 border-b border-gray-700">
                     <h1 className="text-3xl font-bold text-white">Блюда</h1>
                     {isAdmin && (
                        <div className="flex gap-3">
                            <button 
                                onClick={handleRepeatAllMenu}
                                disabled={isRepeatingMenu || loadingDishes}
                                className={`inline-flex items-center gap-2 px-4 py-2 font-medium rounded-md text-sm transition ease-in-out duration-150 shadow ${
                                    isRepeatingMenu || loadingDishes 
                                        ? 'bg-gray-500 cursor-not-allowed text-gray-300' 
                                        : 'bg-green-600 hover:bg-green-700 text-white'
                                }`}
                            >
                                <ArrowPathIcon className={`h-5 w-5 ${isRepeatingMenu ? 'animate-spin' : ''}`} />
                                <span>{isRepeatingMenu ? 'Повторяется...' : 'Повторить меню'}</span>
                            </button>
                            <button 
                                onClick={handleAddClick}
                                className="inline-flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white font-medium rounded-md text-sm transition ease-in-out duration-150 shadow"
                            >
                                <PlusCircleIcon className="h-5 w-5" />
                                <span>Добавить блюдо</span>
                            </button>
                        </div>
                     )}
                </div>

                {/* Статус повторения меню */}
                {repeatMenuStatus.message && (
                    <div className={`p-4 mb-6 rounded-md text-sm flex items-center gap-3 ${
                        repeatMenuStatus.type === 'success' 
                            ? 'bg-green-900 bg-opacity-70 border border-green-700 text-green-300' 
                            : 'bg-red-900 bg-opacity-70 border border-red-700 text-red-300'
                    }`}>
                        <span className="text-lg">
                            {repeatMenuStatus.type === 'success' ? '✅' : '❌'}
                        </span>
                        <span>{repeatMenuStatus.message}</span>
                    </div>
                )}

                {isAdmin && showForm && (
                    <div className="mb-8 relative bg-gray-800 bg-opacity-50 backdrop-filter backdrop-blur-sm rounded-xl shadow-xl p-1">
                       <button onClick={handleCloseForm} className="absolute top-3 right-3 text-gray-500 hover:text-gray-300 z-10" title="Закрыть форму"> 
                          <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={1.5} stroke="currentColor" className="w-6 h-6">
                             <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
                          </svg>
                       </button>
                        <DishForm 
                            onFormSubmit={handleFormSubmit} 
                            isEditing={!!editingDish}
                            initialData={editingDish} 
                        />
                    </div>
                )}

                <div className="bg-gray-800 bg-opacity-80 backdrop-filter backdrop-blur-sm rounded-xl shadow-2xl p-6 md:p-8">
                    <h2 className="text-xl font-semibold text-white mb-4">Список доступных блюд</h2>
                    {errorDishes && !loadingDishes && <p className="text-red-400 mb-4">{errorDishes}</p>}
                    {loadingDishes && <p className="text-gray-400">Загрузка блюд...</p>}
                    {!loadingDishes && !errorDishes && (
                        dishes.length > 0 ? (
                            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
  {dishes.map(dish => (
    <div
      key={dish.id}
      className={`relative bg-gray-700 p-4 rounded-lg shadow transition-opacity duration-300 ${deletingDishId === dish.id ? 'opacity-50' : 'opacity-100'}`}
    >
      {isAdmin && (
        <div className="absolute top-2 right-2 flex gap-1">
          <button
            onClick={() => handleEditClick(dish)}
            disabled={deletingDishId === dish.id}
            className={`p-1 rounded-full text-gray-400 hover:bg-blue-800 hover:text-blue-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-opacity-75 transition ${deletingDishId === dish.id ? 'cursor-not-allowed' : ''}`}
            title="Редактировать блюдо"
          >
            <PencilSquareIcon className="h-4 w-4" />
          </button>
          <button
            onClick={() => handleDeleteDish(dish.id)}
            disabled={deletingDishId === dish.id}
            className={`p-1 rounded-full text-gray-400 hover:bg-red-800 hover:text-red-200 focus:outline-none focus:ring-2 focus:ring-red-500 focus:ring-opacity-75 transition ${deletingDishId === dish.id ? 'cursor-not-allowed' : ''}`}
            title="Удалить блюдо"
          >
            {deletingDishId === dish.id ? (
              <svg className="animate-spin h-4 w-4 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
              </svg>
            ) : (
              <TrashIcon className="h-4 w-4" />
            )}
          </button>
        </div>
      )}

      <h3 className="text-lg font-semibold text-white mb-2 pr-12">{dish.name}</h3>

      {dish.imageUrl && (
        <img src={dish.imageUrl} alt={dish.name} className="w-full h-32 object-cover rounded mb-2" />
      )}

      <p className="text-sm text-gray-300 mb-1">Цена: {dish.price} руб.</p>
      {dish.dishClass && (
        <p className="text-sm text-teal-400 mb-1">Класс: {dish.dishClass}</p>
      )}

      <p className="text-xs text-gray-400">{dish.ingredients || 'Нет описания'}</p>

{dish.days && dish.days.length > 0 && (
  <p className="text-xs text-gray-400">Доступно в дни: {dish.days.join(', ')}</p>
)}

    </div>
  ))}
</div>

                        ) : (
                            <p className="text-gray-500 italic">
                                {errorDishes ? 'Ошибка загрузки.' : 'Нет доступных блюд.'}
                            </p>
                        )
                    )}
                </div>
            </div>
        </div>
    );
};

export default DishList;
